import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Radio } from "lucide-react";
import { API } from "@/lib/api";
import { LOGO_URL } from "@/lib/assets";

const todayStr = () => new Date().toLocaleDateString("en-CA");

export default function LiveResults() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/events`)
      .then((r) => r.json())
      .then((d) => setEvents(Array.isArray(d) ? d : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const today = todayStr();
  const upcoming = events.filter((e) => e.date >= today);
  const past = events.filter((e) => e.date < today).reverse();

  const EventRow = ({ e, dim }) => {
    const live = e.date === today;
    return (
      <motion.div
        data-testid="live-event-card"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        className={`grid grid-cols-[auto_1fr] sm:grid-cols-[110px_1fr_auto] items-center gap-5 sm:gap-8 border border-clay-border px-4 sm:px-6 py-5 transition-colors ${
          live ? "bg-clay-card border-clay-orange/60 clay-glow" : dim ? "opacity-60 hover:opacity-90" : "bg-clay-card/40 hover:bg-clay-card/70"
        }`}
      >
        <div className="text-center">
          <div className={`font-display font-black text-4xl leading-none ${live ? "text-clay-orange" : "text-clay-text"}`}>
            {e.date.slice(8, 10)}
          </div>
          <div className="font-mono text-[9px] tracking-[0.2em] text-clay-muted mt-1 uppercase">
            {new Date(`${e.date}T00:00:00`).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}
          </div>
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-display font-bold uppercase text-xl sm:text-2xl tracking-tight">{e.title}</h3>
            {live && (
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] bg-clay-orange text-clay-bg px-2 py-1 animate-pulse">
                <Radio className="w-3 h-3" /> Live Today
              </span>
            )}
            {e.tag && (
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] border border-clay-gold/40 text-clay-gold px-2 py-0.5">{e.tag}</span>
            )}
          </div>
          <p className="text-clay-muted text-sm mt-1.5">{e.classes}</p>
        </div>
        <div className="col-span-2 sm:col-span-1">
          {e.livegrids_url ? (
            <a
              data-testid="livegrids-open-button"
              href={e.livegrids_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-sm px-6 py-3 transition-all duration-300 hover:-translate-y-0.5"
            >
              <ExternalLink className="w-4 h-4" /> {live ? "Watch Live" : "Grids & Results"}
            </a>
          ) : (
            <span data-testid="livegrids-pending" className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted border border-clay-border px-4 py-3">
              Link added race week
            </span>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <main className="min-h-screen bg-clay-bg">
      <header className="border-b border-clay-border bg-clay-surface/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <a data-testid="live-logo" href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Mid Western Speedway logo" className="w-10 h-10 object-cover rounded-sm ring-1 ring-clay-border" />
            <span className="font-display font-bold uppercase text-lg tracking-wide">
              MWS <span className="text-clay-orange">Live Results</span>
            </span>
          </a>
          <a data-testid="live-back-link" href="/" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-clay-muted hover:text-clay-gold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </a>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-14 lg:py-20">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4">
          Grids, Lineups & Results
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl leading-none mb-6">
          Live Results
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-clay-muted max-w-3xl leading-relaxed mb-6">
          On race day, follow every heat grid, lineup and result as it happens. We publish our meetings on{" "}
          <a href="https://livegrids.com.au" target="_blank" rel="noopener noreferrer" className="text-clay-gold hover:underline">
            LiveGrids.com.au
          </a>{" "}
          — Australia's speedway platform for live grids and results. Each meeting below links straight to its LiveGrids
          page once the club posts it (usually race week).
        </motion.p>

        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted py-16 text-center">Loading meetings…</p>
        ) : (
          <>
            <h2 className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4 mt-10">Upcoming & Current</h2>
            <div className="space-y-3 mb-14">
              {upcoming.length === 0 && (
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted border border-dashed border-clay-border py-10 text-center">
                  No upcoming meetings — season calendar drops soon
                </p>
              )}
              {upcoming.map((e) => (
                <EventRow key={e.id} e={e} dim={false} />
              ))}
            </div>
            {past.length > 0 && (
              <>
                <h2 className="font-mono text-xs uppercase tracking-[0.35em] text-clay-muted mb-4">Past Meetings</h2>
                <div className="space-y-3">
                  {past.map((e) => (
                    <EventRow key={e.id} e={e} dim />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
